import React, { useState } from 'react';
import { logService } from '../../services/logService';
import type { Subject, CreateSubjectInput } from '../../types/log';

const SUBJECT_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
];

interface SubjectFormProps {
  subject: Subject | null;
  onClose: () => void;
  onSuccess: () => void;
}

const SubjectForm: React.FC<SubjectFormProps> = ({ subject, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateSubjectInput>({
    name: subject?.name || '',
    description: subject?.description || '',
    type: subject?.type || 'tag',
    color: subject?.color || SUBJECT_COLORS[0],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      if (subject) {
        await logService.updateSubject(subject.id, formData);
      } else {
        await logService.createSubject(formData);
      }
      onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof CreateSubjectInput, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full max-w-md mx-4 bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-claude-darkBorder border-claude-border">
          <h2 className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
            {subject ? 'Edit Subject' : 'New Subject'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
          >
            <svg
              className="h-5 w-5 dark:text-claude-darkTextSecondary text-claude-textSecondary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter subject name"
              required
              className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary placeholder:text-claude-textSecondary focus:outline-none focus:ring-2 focus:ring-claude-accent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text placeholder:dark:text-claude-darkTextSecondary placeholder:text-claude-textSecondary focus:outline-none focus:ring-2 focus:ring-claude-accent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-1">
              Type
            </label>
            <select
              value={formData.type || 'tag'}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
            >
              <option value="tag">Tag</option>
              <option value="project">Project</option>
              <option value="person">Person</option>
              <option value="location">Location</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {SUBJECT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleChange('color', color)}
                  className={`w-8 h-8 rounded-lg transition-all ${
                    formData.color === color
                      ? 'ring-2 ring-offset-2 ring-claude-accent dark:ring-offset-claude-darkSurface'
                      : 'hover:scale-110'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium dark:text-claude-darkText text-claude-text mb-2">
              Preview
            </label>
            <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-claude-darkBorder border-claude-border">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: formData.color }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium dark:text-claude-darkText text-claude-text truncate">
                  {formData.name || 'Subject Name'}
                </p>
                <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary capitalize">
                  {formData.type || 'tag'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkText text-claude-text hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-claude-accent text-white hover:bg-claude-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : subject ? 'Save Changes' : 'Create Subject'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubjectForm;